/**
 * InboxQueueRail — Fila Única de Trabalho (Inbox Industrial)
 *
 * Coluna esquerda 35% no layout split-screen de Minhas Tarefas.
 * Cards compactos ordenados por criticidade -> SLA -> FIFO.
 * Cada card exibe origem (✦ WORKFLOW / ⚙ AÇÃO / ⚠ PROBLEMA) e badge de prioridade.
 */
import React from 'react';
import { Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import type { TaskListItem } from '../../shared/types/task';
import {
  formatSlaSmartLabel,
  getOperationalRiskLevel,
  getRemainingMinutesFromDeadline,
  getTaskTotalSlaMinutes,
} from '../../shared/utils/operationalRisk';

type OriginKind = 'workflow' | 'action' | 'problem';

function getOrigin(task: TaskListItem): { kind: OriginKind; label: string; symbol: string } {
  const meta = (task.metadata ?? {}) as Record<string, unknown>;
  const src = (meta.source as string | undefined) ?? (meta.workItemSource as string | undefined);
  if (src === 'action') return { kind: 'action', label: 'AÇÃO', symbol: '⚙' };
  if (src === 'problem') return { kind: 'problem', label: 'PROBLEMA', symbol: '⚠' };
  return { kind: 'workflow', label: 'WORKFLOW', symbol: '✦' };
}

function originClasses(kind: OriginKind): string {
  switch (kind) {
    case 'action':
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/40';
    case 'problem':
      return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900/40';
    default:
      return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900/40';
  }
}

function priorityDot(priority?: string | null): string {
  switch (priority) {
    case 'critica':
      return 'bg-rose-500';
    case 'alta':
      return 'bg-amber-500';
    case 'media':
      return 'bg-blue-500';
    case 'baixa':
      return 'bg-slate-400';
    default:
      return 'bg-slate-300';
  }
}

interface InboxQueueRailProps {
  tasks: TaskListItem[];
  selectedTaskId: string | null;
  onSelect: (taskId: string) => void;
  isLoading?: boolean;
  emptyHint?: string;
}

export const InboxQueueRail: React.FC<InboxQueueRailProps> = ({
  tasks,
  selectedTaskId,
  onSelect,
  isLoading,
  emptyHint,
}) => {
  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-[var(--bg-elevated)]">
      {/* Rail Header */}
      <div className="shrink-0 flex items-center justify-between border-b border-slate-200 dark:border-[var(--border-default)] px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">
            Fila de Demandas
          </span>
          <span className="flex h-[18px] min-w-[22px] items-center justify-center rounded-sm bg-slate-900 px-1.5 font-mono text-[10px] font-bold tabular-nums text-white dark:bg-slate-700">
            {tasks.length}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <p className="px-3 py-4 font-mono text-[10px] uppercase tracking-[0.1em] text-slate-400">Carregando…</p>
        ) : tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
            <CheckCircle2 size={20} className="text-emerald-500" />
            <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Fila vazia</p>
            <p className="text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
              {emptyHint ?? 'Operação estável'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-[var(--border-default)]">
            {tasks.map((task) => {
              const origin = getOrigin(task);
              const isSelected = task.id === selectedTaskId;
              const remainingMinutes = getRemainingMinutesFromDeadline(task.slaDeadline);
              const totalSlaMinutes = getTaskTotalSlaMinutes(task);
              const risk = getOperationalRiskLevel(remainingMinutes, totalSlaMinutes);
              const slaInfo = formatSlaSmartLabel(remainingMinutes);
              const code = task.referenceCode ?? task.instanceTitle ?? task.instanceId.slice(0, 8).toUpperCase();
              return (
                <li key={task.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(task.id)}
                    className={`group relative flex w-full items-stretch gap-2 border-l-[3px] px-3 py-2.5 text-left transition-colors ${
                      isSelected
                        ? 'border-l-blue-600 bg-blue-50/70 dark:bg-blue-950/30'
                        : 'border-l-transparent hover:bg-slate-50 dark:hover:bg-slate-900/40'
                    }`}
                  >
                    {/* Priority dot column */}
                    <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${priorityDot(task.priority)}`} aria-hidden />

                    {/* Body */}
                    <div className="min-w-0 flex-1">
                      {/* Origin badge + code */}
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-[1px] font-mono text-[8.5px] font-bold uppercase tracking-[0.1em] ${originClasses(origin.kind)}`}
                        >
                          <span>{origin.symbol}</span>
                          {origin.label}
                        </span>
                        <span className="truncate font-mono text-[9.5px] text-slate-400 dark:text-slate-500">
                          {code}
                        </span>
                      </div>

                      {/* Stage / title */}
                      <p
                        className={`mt-1 truncate text-[11.5px] font-semibold leading-snug ${
                          isSelected
                            ? 'text-blue-900 dark:text-blue-200'
                            : 'text-slate-900 dark:text-[var(--text-primary)]'
                        }`}
                        title={task.stageName}
                      >
                        {task.stageName}
                      </p>

                      {/* SLA + risk */}
                      <div className="mt-1 flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 font-mono text-[9.5px] tabular-nums ${
                            risk === 'CRITICO'
                              ? 'text-rose-600 dark:text-rose-400'
                              : risk === 'ALERTA'
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-slate-500 dark:text-slate-400'
                          }`}
                        >
                          {risk === 'CRITICO' ? <AlertCircle size={10} /> : <Clock size={10} />}
                          {slaInfo.status === 'none' ? 'sem SLA' : slaInfo.label}
                        </span>
                        {task.status === 'in_progress' && (
                          <span className="inline-flex items-center font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-emerald-600 dark:text-emerald-400">
                            ▸ ativa
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default InboxQueueRail;
